import React from "react"
import { Table, TableHeaderColumn, TableRow, TableHeader, TableRowColumn, TableBody, RaisedButton, FontIcon } from "material-ui"
import CustomTable from "../../common/Table"
import strings from "../../../localization/strings"
import autobind from "class-autobind"
import { EScheduleListType, IClientDocument, IJobDocument, IScheduleDocument } from "../../../models"
import DonutChart from "../../common/donutChart/DonutChart"
import * as scheduleHelper from "../../../helpers/scheduleHelper"
import * as timeHelper from "../../../helpers/timeHelper"
import moment from "moment"
import * as permissionHelper from "../../../helpers/permissionHelper"
import * as hierarchyHelper from "../../../helpers/hierarchyHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import { grey300 } from "material-ui/styles/colors"
import ScheduleDetail from "../detail/ScheduleDetail"
import AccountStore from "../../../stores/AccountStore"
import { checkJobFieldAvailable } from "../../../helpers/jobHelper"

interface IProps {
	readonly: boolean
	type: EScheduleListType
	schedules: IScheduleDocument[]
	selectedSchedules: IScheduleDocument[]
	total: number
	offset: number
	limit: number
	limitOptions: number[]
	handleSelect(schedules: IScheduleDocument[]): void
	onPageClick(offset: number): void
	onLimitChange(limit: number): void
	handlePostSchedule(schedule: IScheduleDocument): void
	reload(): void
	handleChangeWithoutApiCall(schedule: IScheduleDocument): void

	job: IJobDocument
	client: IClientDocument
}
interface IState {
	expandedScheduleId: string
}

export default class ScheduleListTable extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)
		this.state = {
			expandedScheduleId: props.schedules.length === 1 ? props.schedules[0].Id : null
		}
		autobind(this)
	}

	render() {
		return (
			<CustomTable
				readonly={this.props.readonly || (!permissionHelper.hasPermission(AuthenticationStore.Permissions.Schedule.DeleteSchedule) && !permissionHelper.hasPermission(AuthenticationStore.Permissions.Schedule.AddSchedule))}
				items={this.props.schedules}
				selectedItems={this.props.selectedSchedules}
				total={this.props.total}
				offset={this.props.offset}
				limit={this.props.limit}
				limitOptions={this.props.limitOptions}
				handleSelect={this.props.handleSelect}
				onPageClick={this.props.onPageClick}
				onLimitChange={this.props.onLimitChange}
			>
				<TableHeader>
					<TableRow className="ShortSelectBoxTableHeader">
						<TableHeaderColumn className="TextOverflow" title={strings.Name} style={{ textAlign: "left", width: 200, paddingLeft: 5, paddingRight: 5 }}>{strings.Name}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Hierarchy} style={{ textAlign: "left", width: 150, paddingLeft: 5, paddingRight: 5 }}>{strings.Hierarchy}</TableHeaderColumn>
						{AccountStore.getJobsEnabled() &&
							<TableHeaderColumn className="TextOverflow" title={checkJobFieldAvailable('singular') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}` : strings.Job} style={{ textAlign: "left", width: 100, paddingLeft: 5, paddingRight: 5 }}>{checkJobFieldAvailable('singular') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}` : strings.Job}</TableHeaderColumn>
						}
						<TableHeaderColumn className="TextOverflow" title={strings.Period_Days} style={{ textAlign: "center", width: 40, paddingLeft: 5, paddingRight: 5 }}>{strings.Period_Days}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Start_Date} style={{ textAlign: "center", width: 80, paddingLeft: 5, paddingRight: 5 }}>{strings.Start_Date}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={this.props.type === EScheduleListType.Master ? strings.Next_Post_Date : strings.End_Date} style={{ textAlign: "center", width: 80, paddingLeft: 5, paddingRight: 5 }}>
							{this.props.type === EScheduleListType.Master ? strings.Next_Post_Date : strings.End_Date}
						</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Description} style={{ textAlign: "left", paddingLeft: 5, paddingRight: 5 }}>{strings.Description}</TableHeaderColumn>
						<TableHeaderColumn className="TextOverflow" title={strings.Total_Shifts} style={{ textAlign: "center", width: 70, paddingLeft: 5, paddingRight: 5 }}>{strings.Total_Shifts}</TableHeaderColumn>
						{!AccountStore.getMedicalAccount() && <TableHeaderColumn className="TextOverflow" title={strings.Unfilled_Shifts} style={{ textAlign: "center", width: 80, paddingLeft: 5, paddingRight: 5 }}>{strings.Unfilled_Shifts}</TableHeaderColumn>}
						<TableHeaderColumn className="TextOverflow" title={strings.Filled_Status} style={{ textAlign: "center", width: 80, paddingLeft: 5, paddingRight: 5 }}>{strings.Filled_Status}</TableHeaderColumn>
						{this.props.type === EScheduleListType.Master ?
							<TableHeaderColumn className="TextOverflow" title={strings.Status} style={{ textAlign: "center", cursor: "pointer", padding: 0, width: 125 }}>{strings.Status}</TableHeaderColumn> :
							[
								<TableHeaderColumn key="agency" className="TextOverflow" title={strings.Agency} style={{ textAlign: "center", width: 80, paddingLeft: 5, paddingRight: 5 }}>{strings.Agency}</TableHeaderColumn>,
								permissionHelper.hasPermission(AuthenticationStore.Permissions.Schedule.EditSchedule) &&
								<TableHeaderColumn key="post" className="TextOverflow" title={strings.Posted} style={{ textAlign: "center", cursor: "pointer", padding: 0, width: 200 }}>{strings.Posted}</TableHeaderColumn>
							]
						}
						<TableHeaderColumn style={{ width: 20, paddingLeft: 5, paddingRight: 5 }} />
					</TableRow>
				</TableHeader>
				<TableBody deselectOnClickaway={false}>
					{this.props.schedules.map((row, index) => {
						const schedules = row.Schedules.filter(i => !i.Deleted)
						const totalShiftsCount = schedules.length
						const unfilledShiftsCount = schedules.filter(pt => !pt.AssignedUserId).length
						const fillRate = (row.Budget && row.Budget.ScheduleBudgetInfo && row.Budget.ScheduleBudgetInfo.FillRateInfo) ? row.Budget.ScheduleBudgetInfo.FillRateInfo.FillRate : 0

						const filledPercent = Math.round(AccountStore.getMedicalAccount() ? fillRate * 100 : ((totalShiftsCount - unfilledShiftsCount) * 100 / totalShiftsCount))
						let agencyPercentage = null
						if (row.Summary) {
							agencyPercentage = Math.round(row.Summary.FilledByAgency * 100) + "%";
						}
						return (
							<TableRow
								key={index}
								data-intercom-target={index === 0 && "menu-Schedules-ItemDetails"}
								className="ShortSelectBoxTableRow"
								selected={this.props.selectedSchedules.find((item) => item.Id === row.Id) ? true : false}
							>
								<TableRowColumn style={{ paddingLeft: 0, paddingRight: 0 }}>
									<Table
										style={{ backgroundColor: "none" }}
										selectable={false}
										onCellClick={() =>
											this.setState({ expandedScheduleId: this.state.expandedScheduleId === row.Id ? null : row.Id })
										}
									>
										<TableBody displayRowCheckbox={false}>
											<TableRow style={{ cursor: "pointer", height: 48, borderBottom: "none" }}
											>
												<TableRowColumn style={{ textAlign: "left", cursor: "pointer", width: 200, paddingLeft: 5, paddingRight: 5 }}>
													<div className="TextOverflow" title={row.Name}>{row.Name}</div>
												</TableRowColumn>
												<TableRowColumn style={{ textAlign: "left", cursor: "pointer", width: 150, paddingLeft: 5, paddingRight: 5 }}>
													<div className="TextOverflow" title={hierarchyHelper.getFullyQualifiedLevelNameById(row.HierarchyId)}>{hierarchyHelper.getFullyQualifiedLevelNameById(row.HierarchyId)}</div>
												</TableRowColumn>
												{AccountStore.getJobsEnabled() &&
													<TableRowColumn style={{ textAlign: "left", cursor: "pointer", width: 100, paddingLeft: 5, paddingRight: 5 }}>
														<div className="TextOverflow" title={row.JobName}>{row.JobName}</div>
													</TableRowColumn>
												}
												<TableRowColumn style={{ textAlign: "center", cursor: "pointer", width: 40, paddingLeft: 5, paddingRight: 5 }}>
													{scheduleHelper.getDayLengthFromStartAndEndTime(row.StartDate, row.EndDate)}
												</TableRowColumn>
												<TableRowColumn style={{ textAlign: "center", cursor: "pointer", width: 80, paddingLeft: 5, paddingRight: 5 }}>
													{moment(timeHelper.keepConvertUTCToLocal(row.StartDate)).format("MM/DD/YYYY")}
												</TableRowColumn>
												<TableRowColumn style={{ textAlign: "center", cursor: "pointer", width: 80, paddingLeft: 5, paddingRight: 5 }}>
													{moment(timeHelper.keepConvertUTCToLocal(this.props.type === EScheduleListType.Master ? row.NextPostDate : row.EndDate)).format("MM/DD/YYYY")}
												</TableRowColumn>
												<TableRowColumn style={{ textAlign: "left", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>
													<div className="TextOverflow" title={row.Description}>{row.Description}</div>
												</TableRowColumn>
												<TableRowColumn style={{ textAlign: "center", cursor: "pointer", width: 70, paddingLeft: 5, paddingRight: 5 }}>
													{totalShiftsCount ? totalShiftsCount : "----"}
												</TableRowColumn>
												{!AccountStore.getMedicalAccount() &&
													<TableRowColumn style={{ textAlign: "center", cursor: "pointer", width: 80, paddingLeft: 5, paddingRight: 5 }}>
														{totalShiftsCount ? unfilledShiftsCount : "----"}
													</TableRowColumn>
												}
												<TableRowColumn style={{ textAlign: "center", cursor: "pointer", width: 80, paddingLeft: 5, paddingRight: 5 }}>
													{totalShiftsCount ?
														<DonutChart
															value={filledPercent}
															valueLabelYPosition={24}
															strokeWidth={4}
															size={40}
															textValStyle={{ fontSize: 12 }}
															percentStyle={{ fontSize: 12 }}
															textLabelStyle={{ display: "none" }}
															spaceBetweenValAndLabel={0}
														/> :
														"----"
													}
												</TableRowColumn>
												{this.props.type === EScheduleListType.Master ?
													<TableRowColumn style={{ textAlign: "center", cursor: "pointer", padding: 0, width: 125 }}>
														{row.MasterScheduleActive ? strings.Active : strings.Inactive}
													</TableRowColumn> :
													[
														<TableRowColumn key="agency" style={{ textAlign: "center", cursor: "pointer", width: 80, paddingLeft: 5, paddingRight: 5 }}>
															{agencyPercentage}
														</TableRowColumn>,
														permissionHelper.hasPermission(AuthenticationStore.Permissions.Schedule.EditSchedule) &&
														<TableRowColumn key="post" style={{ textAlign: "center", cursor: "pointer", padding: 0, width: 200 }}>
															{row.PublishInfo && row.PublishInfo.Published ?
																<div>
																	<span>{moment(row.PublishInfo.Date).format("hh:mm a MM/DD/YYYY")}</span>
																	<br />
																	<span>
																		{`by ${row.PublishInfo.PublishedByUserName}`}
																	</span>
																</div> :
																totalShiftsCount ?
																	<RaisedButton
																		secondary
																		label={strings.Post}
																		disabled={this.props.readonly}
																		disabledBackgroundColor={grey300}
																		disabledLabelColor={"white"}
																		icon={
																			<FontIcon
																				className="fa fa-bullhorn"
																				style={{ transform: "rotate(-20deg)", fontSize: 16 }}
																			/>
																		}
																		onClick={e => {
																			e.stopPropagation()
																			!this.props.readonly &&
																				this.props.handlePostSchedule(row)
																		}}
																	/> :
																	"----"
															}
														</TableRowColumn>
													]
												}
												<TableRowColumn style={{ width: 20, paddingLeft: 5, paddingRight: 5 }}>
													<i
														data-intercom-target={index === 0 && "menu-Schedules-ExpandOrCollapseItemDetails"}
														className={this.state.expandedScheduleId === row.Id ? "fa fa-chevron-up" : "fa fa-chevron-down"}
														style={{ fontSize: 18 }}
													/>
												</TableRowColumn>
											</TableRow>
											{this.state.expandedScheduleId === row.Id &&
												<TableRow >
													<TableRowColumn
														colSpan={this.props.type !== EScheduleListType.Master && permissionHelper.hasPermission(AuthenticationStore.Permissions.Schedule.EditSchedule) ? 12 : 11}
														style={{ paddingRight: 0, paddingLeft: 0 }}
													>
														<div onClick={(e) => { e.stopPropagation() }}>
															<ScheduleDetail
																type={this.props.type}
																readonly={this.props.readonly}
																schedule={row}
																reload={this.props.reload}
																handleChangeWithoutApiCall={this.props.handleChangeWithoutApiCall}
																handlePostMasterScheduleForCurrentWeek={() =>
																	!this.props.readonly &&
																	this.props.handlePostSchedule(row)
																}
																job={this.props.job}
																client={this.props.client}
															/>
														</div>
													</TableRowColumn>
												</TableRow>
											}
										</TableBody>
									</Table>
								</TableRowColumn>
							</TableRow>
						)
					})}
				</TableBody>
			</CustomTable>
		)
	}
}
